import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import logoImg from '@/assets/logo.png';
import { useOrganization } from '@/hooks/useOrganization';

interface AppHeaderProps {
  actions?: React.ReactNode;
}

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Vagas', path: '/jobs' },
  { label: 'Relatórios', path: '/reports' },
  { label: 'Assistente IA', path: '/assistant' },
  { label: 'Integrações', path: '/integrations' },
];

const ADMIN_NAV = [
  { label: 'Usuários', path: '/users', roles: ['owner', 'admin'] },
  { label: 'Configurações', path: '/settings', roles: ['owner'] },
];

export function AppHeader({ actions }: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { org } = useOrganization();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header className="bg-[#0D1B2A] border-b border-[#1a2d42] sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Nav */}
          <div className="flex items-center gap-8">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/dashboard')}
            >
              <img src={logoImg} alt="TalentumIQ" className="h-9 object-contain" />
              {org?.logoUrl && (
                <>
                  <span className="text-gray-500 text-sm">|</span>
                  <img src={org.logoUrl} alt={org.name || 'Empresa'} className="h-10 w-10 object-contain rounded" />
                </>
              )}
            </div>

            <nav className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#00C2C7]/15 text-[#00C2C7] font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              {ADMIN_NAV.filter((item) => user?.role && item.roles.includes(user.role)).map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#00C2C7]/15 text-[#00C2C7] font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {actions}
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-400 border-l border-gray-700 pl-3">
              <div className="h-8 w-8 rounded-full bg-[#00C2C7]/15 flex items-center justify-center text-[#00C2C7] font-medium text-xs">
                {user?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="leading-tight">
                <p className="text-xs font-medium text-white">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white hover:bg-white/5">
              Sair
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
